import { Fragment, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Combobox, Transition } from '@headlessui/react';
import { useAuth } from '../../../context/AuthContext';
import { useCart, CartItem } from '../../../context/CartContext';
import { useZarazTracking } from '../../../lib/zaraz/useZarazTracking';
import { motion } from 'framer-motion';
import { T } from '@tolgee/react';
import api from '../../../lib/api';
import env from '../../../config/env';
import type { DropShippingEmailSettings } from '../../../lib/api/services/auth';
import { getPrivateCustomersLabel, isDistributorRole } from '../../commercial/utils/operator';

const CheckoutPage = () => {
  const { cart } = useCart();
  const loading = cart.isLoading;
  const { auth } = useAuth();
  const navigate = useNavigate();
  const {
    trackCheckoutStarted,
    trackPaymentInfoEntered,
    trackShippingInfoEntered,
    trackOrderCompleted
  } = useZarazTracking();

  // No refetch on mount - the CartContext already has the updated data
  // The context has listeners for focus/visibility/bfcache that will refresh when needed
  useEffect(() => {
    console.log('🛒 CheckoutPage: Mounted, cart has', cart.items.length, 'items, totalItems:', cart.totalItems);
  }, []);

  const clearCart = async () => {
    try {
      const response = await api.cart.clearCart();
      if (response.success && response.data) {
        window.dispatchEvent(new CustomEvent('jarad-cart-updated', { detail: response.data }));
      }
    } catch (error) {
      console.error('Error clearing cart:', error);
    }
  };

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    address: '',
    city: '',
    postalCode: '',
    country: 'España',
    paymentMethod: 'bank_transfer'
  });
  const [orderNotes, setOrderNotes] = useState('');
  const [shippingMethod, setShippingMethod] = useState<'standard' | 'express'>('standard');
  const [shippingCost, setShippingCost] = useState<number>(8);
  const [shippingEtaDays, setShippingEtaDays] = useState<number>(7);
  const [isQuoting, setIsQuoting] = useState<boolean>(false);

  // Commercial-specific: client selector
  const isDistributor = isDistributorRole(auth.user?.role);
  const isCommercial = auth.user?.role === 'commercial';
  const [registeredClients, setRegisteredClients] = useState<any[]>([]);
  const [privateClients, setPrivateClients] = useState<any[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [loadingClients, setLoadingClients] = useState(false);
  const [selectedClientData, setSelectedClientData] = useState<{ name: string; email: string; phone?: string; nif?: string; address?: any } | null>(null);
  const [dropShippingSettings, setDropShippingSettings] = useState<DropShippingEmailSettings | null>(null);
  const [dropShippingEnabled, setDropShippingEnabled] = useState(isDistributor);
  const [notifyRecipient, setNotifyRecipient] = useState(false);
  const [recipientData, setRecipientData] = useState<{ name: string; email: string; phone?: string; notes?: string } | null>(null);
  const [recipientSearchQuery, setRecipientSearchQuery] = useState('');

  useEffect(() => {
    setDropShippingEnabled(isDistributor);
  }, [isDistributor]);

  // Fetch commercial's clients
  useEffect(() => {
    const fetchClients = async () => {
      if (!isCommercial && !isDistributor) return;
      setLoadingClients(true);
      try {
        if (isCommercial) {
          const regRes = await api.users.getMyClients();
          if (regRes.success && regRes.data) setRegisteredClients(regRes.data);
        } else {
          setRegisteredClients([]);
        }
        const privRes = await api.users.getMyPrivateClients();
        if (privRes.success && privRes.data) setPrivateClients(privRes.data);
        if (isDistributor) {
          const settingsRes = await api.auth.getDropShippingEmailSettings();
          if (settingsRes.success && settingsRes.data) {
            setDropShippingSettings(settingsRes.data);
            setNotifyRecipient(settingsRes.data.recipientEmailsEnabledByDefault);
          }
        }
      } catch (err) {
        console.error('Error fetching clients:', err);
      } finally {
        setLoadingClients(false);
      }
    };
    fetchClients();
  }, [isCommercial, isDistributor]);

  // Handle client selection for commercial
  const handleClientSelect = (clientId: string) => {
    setSelectedClientId(clientId);
    if (!clientId) {
      setSelectedClientData(null);
      setRecipientSearchQuery('');
      if (isDistributor) {
        setRecipientData(null);
      }
      return;
    }

    // Check registered clients first
    let selected = registeredClients.find((c: any) => c.id === clientId || c._id === clientId);
    if (selected) {
      setSelectedClientData({
        name: selected.name || '',
        email: selected.email || '',
        phone: selected.phone || '',
        nif: selected.nif || '',
        address: selected.address || null
      });
      if (isDistributor) {
        setRecipientSearchQuery(selected.name || '');
        setRecipientData({
          name: selected.name || '',
          email: selected.email || '',
          phone: selected.phone || '',
          notes: selected.notes || ''
        });
      }
      // Auto-fill shipping address
      if (selected.address) {
        setFormData(prev => ({
          ...prev,
          address: selected.address.street || prev.address,
          city: selected.address.city || prev.city,
          postalCode: selected.address.postalCode || prev.postalCode,
          country: selected.address.country || prev.country
        }));
      }
      return;
    }

    // Check private clients
    selected = privateClients.find((c: any) => c._id === clientId);
    if (selected) {
      setSelectedClientData({
        name: selected.name || '',
        email: selected.email || '',
        phone: selected.phone || '',
        nif: selected.nif || '',
        address: selected.address || null
      });
      if (isDistributor) {
        setRecipientSearchQuery(selected.name || '');
        setRecipientData({
          name: selected.name || '',
          email: selected.email || '',
          phone: selected.phone || '',
          notes: selected.notes || ''
        });
      }
      if (selected.address) {
        setFormData(prev => ({
          ...prev,
          address: selected.address.street || prev.address,
          city: selected.address.city || prev.city,
          postalCode: selected.address.postalCode || prev.postalCode,
          country: selected.address.country || prev.country
        }));
      }
    }
  };


  // Prellenar datos con la información del usuario autenticado
  useEffect(() => {
    if (auth.user) {
      const nameParts = auth.user.name.split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      setFormData(prev => ({
        ...prev,
        firstName,
        lastName,
        email: auth.user?.email || prev.email
      }));
    }
  }, [auth.user]);

  // Trackear inicio del checkout
  useEffect(() => {
    if (cart.items.length > 0) {
      const total = cart.subtotal;
      const revenue = cart.subtotal;

      trackCheckoutStarted({
        total,
        revenue,
        currency: 'EUR',
        value: total
      });
    }
  }, [cart, trackCheckoutStarted]);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  const [createdOrderId, setCreatedOrderId] = useState<string | null>(null);
  const [orderSummary, setOrderSummary] = useState<{
    items: CartItem[],
    subtotal: number,
    shippingCost: number,
    total: number,
    shippingAddress: { street: string; city: string; postalCode: string; country: string },
    email: string
  } | null>(null);

  const selectedSavedRecipient = isDistributor
    ? privateClients.find((client: any) => client._id === selectedClientId)
    : null;

  const filteredPrivateClients = recipientSearchQuery.trim() === ''
    ? privateClients
    : privateClients.filter((client: any) => {
      const searchableText = [
        client.name,
        client.email,
        client.phone,
        client.address?.street,
        client.address?.city,
        client.address?.postalCode,
        client.address?.country,
        client.notes
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return searchableText.includes(recipientSearchQuery.trim().toLowerCase());
    });

  const formatSavedRecipientLocation = (client: any) => {
    if (!client?.address) {
      return 'Sin dirección guardada';
    }

    const parts = [
      client.address.street,
      client.address.city,
      client.address.postalCode
    ].filter(Boolean);

    return parts.length > 0 ? parts.join(', ') : 'Sin dirección guardada';
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    // Limpiar error cuando se modifica un campo
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }

    // Trackear cuando se completa información de envío
    if (name === 'address' || name === 'city' || name === 'postalCode') {
      if (formData.address && formData.city && formData.postalCode) {
        const total = cart?.subtotal || 0;
        trackShippingInfoEntered({
          total,
          revenue: cart?.subtotal || 0,
          currency: 'EUR',
          value: total
        });
      }
    }

    // Trackear cuando se selecciona método de pago
    if (name === 'paymentMethod') {
      const total = cart?.subtotal || 0;
      trackPaymentInfoEntered(
        { payment_type: value },
        {
          total,
          revenue: cart?.subtotal || 0,
          currency: 'EUR',
          value: total
        }
      );
    }
  };

  // Cotizar envío cuando cambian dirección, país, postal o contenido del carrito
  useEffect(() => {
    const quote = async () => {
      if (cart.items.length === 0) return;
      if (!formData.address || !formData.city || !formData.postalCode || !formData.country) return;
      setIsQuoting(true);
      try {
        const payload = {
          items: cart.items.map((it) => ({ productId: it.productId, quantity: it.quantity })),
          shippingAddress: {
            country: formData.country,
            postalCode: formData.postalCode,
            city: formData.city,
            street: formData.address
          }
        };
        const res = await api.orders.shippingQuote(payload);
        if (res.success && res.data) {
          setShippingCost(res.data.shippingCost);
          setShippingEtaDays(res.data.etaDays);
        }
      } catch (err) {
        // mantener último coste si falla
      } finally {
        setIsQuoting(false);
      }
    };
    quote();
  }, [cart, formData.address, formData.city, formData.postalCode, formData.country]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim()) newErrors.firstName = 'checkout.errors.first_name_required';
    if (!formData.lastName.trim()) newErrors.lastName = 'checkout.errors.last_name_required';
    if (!formData.email.trim()) {
      newErrors.email = 'checkout.errors.email_required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'checkout.errors.email_invalid';
    }
    if (!formData.address.trim()) newErrors.address = 'checkout.errors.address_required';
    if (!formData.city.trim()) newErrors.city = 'checkout.errors.city_required';
    if (!formData.postalCode.trim()) {
      newErrors.postalCode = 'checkout.errors.postal_code_required';
    } else if (!/^\d{5}$/.test(formData.postalCode)) {
      newErrors.postalCode = 'checkout.errors.postal_code_invalid';
    }

    if (isDistributor && dropShippingEnabled) {
      if (!recipientData?.name?.trim()) {
        newErrors.recipientName = 'El nombre del destinatario final es obligatorio';
      }
      if (notifyRecipient && !recipientData?.email?.trim()) {
        newErrors.recipientEmail = 'El email del destinatario es obligatorio si quieres notificarle';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Commercial must select a client
    if (isCommercial && !selectedClientData) {
      setErrors({ form: 'Debes seleccionar un cliente para crear el pedido' });
      return;
    }

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      // Construir payload del pedido para transferencia bancaria
      const orderPayload: any = {
        products: cart.items.map(item => ({
          productId: item.productId,
          quantity: item.quantity,
          variantSku: item.variantId || undefined
        })),
        shippingAddress: {
          street: formData.address,
          city: formData.city,
          postalCode: formData.postalCode,
          country: formData.country,
        },
        ...(!isDistributor ? {
          billingAddress: {
            street: formData.address,
            city: formData.city,
            postalCode: formData.postalCode,
            country: formData.country,
          }
        } : {}),
        shippingMethod: shippingMethod,
        paymentMethod: 'bank_transfer',
        notes: orderNotes,
        // Include customerInfo for commercial orders
        ...(isCommercial && selectedClientData ? {
          customerInfo: {
            name: selectedClientData.name,
            email: selectedClientData.email,
            phone: selectedClientData.phone || '',
            nif: selectedClientData.nif || ''
          }
        } : {}),
        ...(isDistributor && dropShippingEnabled && recipientData ? {
          dropShipping: {
            enabled: true,
            notifyRecipient,
            recipient: {
              ...recipientData,
              address: {
                street: formData.address,
                city: formData.city,
                postalCode: formData.postalCode,
                country: formData.country,
              }
            }
          }
        } : {})
      };

      const response = await api.orders.createOrder(orderPayload);
      if (!response.success || !response.data) {
        // Extraer mensaje de error del backend (puede venir en distintos formatos)
        const res = response as any;
        let errorMsg = response.error?.message || res.message || '';
        if (res.errors && Array.isArray(res.errors)) {
          errorMsg = res.errors.map((e: any) => e.message || e.field).join('. ');
        }
        throw new Error(errorMsg || 'Error al crear el pedido');
      }

      const newOrderId = (response.data as any)._id || (response.data as any).id || null;
      setCreatedOrderId(newOrderId);

      // Guardar resumen para PDF antes de limpiar el carrito
      const shippingCostCalc = shippingCost;
      const total = Number((response.data as any).totalAmount ?? cart.subtotal);
      setOrderSummary({
        items: cart.items,
        subtotal: cart.subtotal,
        shippingCost: shippingCostCalc,
        total,
        shippingAddress: {
          street: formData.address,
          city: formData.city,
          postalCode: formData.postalCode,
          country: formData.country
        },
        email: formData.email
      });

      // Trackear orden completada
      trackOrderCompleted({
        order_id: newOrderId,
        total,
        revenue: cart.subtotal,
        currency: 'EUR',
        value: total
      });

      setOrderComplete(true);
      clearCart();
    } catch (error: any) {
      console.error('Error al procesar el pedido:', error);
      setErrors({
        form: error?.message || 'Error al procesar el pedido. Inténtalo de nuevo.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleContinueShopping = () => {
    navigate('/products');
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="animate-pulse text-gray-400">
          <T keyName="checkout.loading">Cargando...</T>
        </div>
      </div>
    );
  }

  // Si el carrito está vacío, redirigir a la página del carrito
  if (cart.items.length === 0 && !orderComplete) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center py-16">
          <h2 className="text-3xl font-extrabold text-gray-900 mb-4">
            <T keyName="checkout.cart_empty_title">Tu carrito está vacío</T>
          </h2>
          <p className="text-lg text-gray-500 mb-8">
            <T keyName="checkout.cart_empty_message">No puedes realizar un pedido sin productos en el carrito.</T>
          </p>
          <button
            onClick={() => navigate('/cart')}
            className="inline-flex items-center px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-black hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black"
          >
            <T keyName="checkout.go_to_cart">Ir al carrito</T>
          </button>
        </div>
      </div>
    );
  }

  // Si el pedido se ha completado con éxito
  if (orderComplete) {
    const handleDownloadPDF = () => {
      const w = window.open('', 'PRINT', 'height=800,width=700');
      if (!w) return;
      const now = new Date();
      const id = createdOrderId || '—';
      const holder = env.BANK_HOLDER || 'Jarad Pet Food S.L.';
      const iban = env.BANK_IBAN || '';
      const concept = `PED-${id}`;
      const itemsHtml = (orderSummary?.items || []).map((item) => {
        const pbd = (item as any).priceBeforeDiscount;
        const dInfo = (item as any).discountInfo;
        const hasDiscount = pbd && pbd > item.price;
        const priceCell = hasDiscount
          ? `<span style="text-decoration:line-through;color:#999;">€${pbd.toFixed(2)}</span> <span style="color:#16a34a;font-weight:600;">€${(item.price || 0).toFixed(2)}</span>${dInfo ? ` <span style="font-size:11px;color:#dc2626;">${dInfo.displayValue}</span>` : ''}`
          : `€${(item.price || 0).toFixed(2)}`;
        const totalCell = hasDiscount
          ? `<span style="text-decoration:line-through;color:#999;">€${(pbd * item.quantity).toFixed(2)}</span> <span style="color:#16a34a;font-weight:600;">€${((item.price || 0) * item.quantity).toFixed(2)}</span>`
          : `€${((item.price || 0) * item.quantity).toFixed(2)}`;
        return `
        <tr>
          <td style="padding:8px;border-bottom:1px solid #eee;">${item.title}</td>
          <td style="padding:8px;border-bottom:1px solid #eee;">${item.quantity}</td>
          <td style="padding:8px;border-bottom:1px solid #eee;">${priceCell}</td>
          <td style="padding:8px;border-bottom:1px solid #eee;">${totalCell}</td>
        </tr>`;
      }).join('');
      const totalSavings = (orderSummary?.items || []).reduce((acc, item) => {
        const pbd = (item as any).priceBeforeDiscount;
        if (pbd && pbd > item.price) return acc + (pbd - item.price) * item.quantity;
        return acc;
      }, 0);
      const html = `
        <html>
          <head>
            <title>Pedido ${concept}</title>
            <style>
              body{font-family:Arial,sans-serif;color:#111;}
              .container{max-width:800px;margin:0 auto;padding:24px;}
              h1{font-size:20px;margin:0 0 8px}
              h2{font-size:16px;margin:16px 0 8px}
              table{width:100%;border-collapse:collapse}
              .totals td{padding:6px}
            </style>
          </head>
          <body>
            <div class="container">
              <h1>Confirmación de pedido / Order Confirmation</h1>
              <p><strong>Pedido / Order:</strong> ${concept}<br/>
                 <strong>Fecha / Date:</strong> ${now.toLocaleDateString('es-ES')} ${now.toLocaleTimeString('es-ES')}<br/>
                 <strong>Email:</strong> ${orderSummary?.email || ''}
              </p>
              <h2>Dirección de envío / Shipping Address</h2>
              <p>
                ${orderSummary?.shippingAddress.street}<br/>
                ${orderSummary?.shippingAddress.postalCode} ${orderSummary?.shippingAddress.city}<br/>
                ${orderSummary?.shippingAddress.country}
              </p>
              <h2>Productos / Products</h2>
              <table>
                <thead>
                  <tr>
                    <th style="text-align:left;padding:8px;border-bottom:1px solid #ccc;">Producto / Product</th>
                    <th style="text-align:left;padding:8px;border-bottom:1px solid #ccc;">Cantidad / Qty</th>
                    <th style="text-align:left;padding:8px;border-bottom:1px solid #ccc;">Precio / Price</th>
                    <th style="text-align:left;padding:8px;border-bottom:1px solid #ccc;">Total</th>
                  </tr>
                </thead>
                <tbody>
                  ${itemsHtml}
                </tbody>
              </table>
              <table class="totals" style="margin-top:12px;">
                <tr><td><strong>Subtotal</strong></td><td style="text-align:right">€${orderSummary?.subtotal.toFixed(2)}</td></tr>
                ${totalSavings > 0 ? `<tr><td style="color:#16a34a;"><strong>Descuento aplicado / Discount</strong></td><td style="text-align:right;color:#16a34a;">-€${totalSavings.toFixed(2)}</td></tr>` : ''}
                <tr><td><strong>Total</strong></td><td style="text-align:right">€${orderSummary?.total.toFixed(2)}</td></tr>
                ${auth.user?.prontoPagoDiscount && auth.user.prontoPagoDiscount > 0 ? `
                <tr style="background:#fffbeb;">
                  <td style="padding:6px;color:#92400e;"><strong>Con pronto pago (${auth.user.prontoPagoDiscount}%)</strong></td>
                  <td style="text-align:right;padding:6px;color:#92400e;font-weight:bold;">€${((orderSummary?.total || 0) * (1 - auth.user.prontoPagoDiscount / 100)).toFixed(2)}</td>
                </tr>` : ''}
              </table>
              <h2>Instrucciones de transferencia / Bank Transfer Instructions</h2>
              <p>
                <strong>Titular / Account Holder:</strong> ${holder}<br/>
                ${iban ? `<strong>IBAN:</strong> ${iban}<br/>` : ''}
                <strong>Concepto / Reference:</strong> ${concept}
              </p>
            </div>
            <script>window.onload = function(){window.print();}</script>
          </body>
        </html>`;
      w.document.write(html);
      w.document.close();
      w.focus();
    };
    return (
      <motion.div
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div className="bg-white shadow overflow-hidden sm:rounded-lg p-8 text-center">
          <div className="mb-6">
            <svg className="mx-auto h-16 w-16 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-3xl font-extrabold text-gray-900 mb-4">
            <T keyName="checkout.order_completed_title">¡Pedido completado!</T>
          </h2>
          <p className="text-lg text-gray-500 mb-8">
            <T keyName="checkout.order_completed_message">Gracias por tu compra. Te hemos enviado un correo con las instrucciones para realizar la transferencia.</T>
          </p>
          <div className="max-w-xl mx-auto text-left bg-gray-50 p-6 rounded-lg border border-gray-200 mb-8">
            <h3 className="text-md font-semibold text-gray-900 mb-3">
              <T keyName="checkout.payment_instructions_title">Instrucciones de pago por transferencia</T>
            </h3>
            <p className="text-sm text-gray-700">
              <span className="font-medium"><T keyName="checkout.bank_holder">Titular</T>:</span> {env.BANK_HOLDER}
            </p>
            {env.BANK_IBAN && (
              <p className="text-sm text-gray-700">
                <span className="font-medium">IBAN:</span> {env.BANK_IBAN}
              </p>
            )}
            <p className="text-sm text-gray-700">
              <span className="font-medium"><T keyName="checkout.bank_reference">Concepto</T>:</span> {createdOrderId ? `PED-${createdOrderId}` : <T keyName="checkout.order_reference">Referencia de tu pedido</T>}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={handleDownloadPDF}
              className="inline-flex items-center px-6 py-3 border border-gray-300 rounded-md shadow-sm text-base font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black"
            >
              <T keyName="checkout.download_pdf">Descargar PDF</T>
            </button>
            <button
              onClick={() => navigate('/orders')}
              className="inline-flex items-center px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-gray-800 hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black"
            >
              <T keyName="checkout.view_my_orders">Ver mis pedidos</T>
            </button>
            <button
              onClick={handleContinueShopping}
              className="inline-flex items-center px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-black hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black"
            >
              <T keyName="checkout.continue_shopping">Seguir comprando</T>
            </button>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="checkout-page">
      <h1 className="text-3xl font-extrabold tracking-tight text-gray-900 mb-8">
        <T keyName="checkout.title">Finalizar compra</T>
      </h1>

      <div className="lg:grid lg:grid-cols-2 lg:gap-x-12 xl:gap-x-16">
        {/* Formulario */}
        <div>
          <form onSubmit={handleSubmit} data-testid="checkout-form">
            {/* Client/Recipient Selector for B2B operators */}
            {(isCommercial || isDistributor) && (
              <div className="bg-white shadow overflow-visible sm:rounded-lg p-6 mb-8 border border-gray-200 relative z-10">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-5">
                  <div>
                    <h2 className="text-lg font-medium text-gray-900">
                      {isDistributor ? 'Seleccionar cliente final' : <T keyName="checkout.select_client">Seleccionar Cliente</T>}
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                      {isDistributor
                        ? 'Busca por nombre, email, teléfono o dirección para reutilizar un cliente final guardado.'
                        : <T keyName="checkout.commercial_notice">Como comercial, debes seleccionar el cliente para el que estás creando este pedido.</T>}
                    </p>
                  </div>

                  {isDistributor && selectedClientId && (
                    <button
                      type="button"
                      onClick={() => handleClientSelect('')}
                      className="inline-flex items-center justify-center rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Cliente final nuevo
                    </button>
                  )}
                </div>

                {isDistributor ? (
                  <>
                    <Combobox
                      value={selectedSavedRecipient || null}
                      onChange={(client: any | null) => handleClientSelect(client?._id || '')}
                    >
                      {({ open }) => (
                        <div className={`relative ${open ? 'z-40' : 'z-10'}`}>
                          <div className="relative mt-1">
                            <Combobox.Input
                              data-testid="saved-recipient-combobox-input"
                              className="w-full rounded-md border border-gray-300 py-3 pl-4 pr-12 text-sm shadow-sm focus:border-black focus:outline-none focus:ring-1 focus:ring-black"
                              onChange={(event) => setRecipientSearchQuery(event.target.value)}
                              displayValue={(client: any) => client?.name || ''}
                              placeholder={loadingClients ? 'Cargando clientes finales...' : 'Buscar cliente final guardado'}
                            />
                            <Combobox.Button className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400">
                              <svg className={`h-5 w-5 transition-transform ${open ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                              </svg>
                            </Combobox.Button>
                          </div>

                          <Transition
                            as={Fragment}
                            leave="transition ease-in duration-100"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                            afterLeave={() => setRecipientSearchQuery('')}
                          >
                            <Combobox.Options className="absolute left-0 right-0 z-50 mt-2 max-h-72 overflow-auto rounded-xl border border-gray-200 bg-white py-2 shadow-2xl ring-1 ring-black/5 focus:outline-none">
                              {loadingClients && (
                                <div className="px-4 py-3 text-sm text-gray-500">Cargando clientes finales...</div>
                              )}

                              {!loadingClients && filteredPrivateClients.length === 0 && (
                                <div className="px-4 py-3 text-sm text-gray-500">
                                  {privateClients.length === 0
                                    ? 'Todavía no tienes clientes finales guardados.'
                                    : 'No hay resultados para esa búsqueda.'}
                                </div>
                              )}

                              {!loadingClients && filteredPrivateClients.map((client: any) => (
                                <Combobox.Option
                                  key={client._id}
                                  value={client}
                                  data-testid={`saved-recipient-option-${client._id}`}
                                  className={({ active }) =>
                                    `cursor-pointer px-4 py-3 ${active ? 'bg-gray-50' : 'bg-white'}`
                                  }
                                >
                                  {({ selected }) => (
                                    <div className="flex items-start justify-between gap-3">
                                      <div className="min-w-0">
                                        <p className="text-sm font-medium text-gray-900 truncate">{client.name}</p>
                                        <p className="text-sm text-gray-500 truncate">
                                          {client.email || 'Sin email guardado'}
                                        </p>
                                        <p className="mt-1 text-xs text-gray-500">
                                          {formatSavedRecipientLocation(client)}
                                        </p>
                                      </div>
                                      {selected && (
                                        <span className="mt-0.5 text-black" aria-hidden="true">
                                          <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M16.704 5.29a1 1 0 010 1.42l-7.2 7.2a1 1 0 01-1.415 0l-3-3a1 1 0 111.415-1.42l2.292 2.29 6.492-6.49a1 1 0 011.416 0z" clipRule="evenodd" />
                                          </svg>
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </Combobox.Option>
                              ))}
                            </Combobox.Options>
                          </Transition>

                          {!loadingClients && selectedSavedRecipient && !open && (
                            <div className="mt-3 px-1">
                              <p className="text-sm font-medium text-gray-900">{selectedSavedRecipient.email || 'Sin email guardado'}</p>
                              <p className="text-xs text-gray-500 mt-1">{formatSavedRecipientLocation(selectedSavedRecipient)}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </Combobox>

                    {!loadingClients && privateClients.length === 0 && (
                      <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-4">
                        <p className="text-sm font-medium text-gray-900">Todavía no tienes clientes finales guardados</p>
                        <p className="text-sm text-gray-500 mt-1">
                          Puedes continuar con un cliente final nuevo y, si quieres, guardarlo más adelante desde tu panel.
                        </p>
                      </div>
                    )}

                  </>
                ) : (
                  <>
                    <select
                      value={selectedClientId}
                      onChange={(e) => handleClientSelect(e.target.value)}
                      className={`w-full px-3 py-2 border ${selectedClientData ? 'border-green-300 bg-green-50' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-black`}
                      disabled={loadingClients}
                    >
                      <option value="">{isDistributor ? '-- Selecciona un destinatario --' : '-- Selecciona un cliente --'}</option>
                      {!isDistributor && registeredClients.length > 0 && (
                        <optgroup label="Clientes registrados">
                          {registeredClients.map((c: any) => (
                            <option key={c.id || c._id} value={c.id || c._id}>{c.name} ({c.email})</option>
                          ))}
                        </optgroup>
                      )}
                      {privateClients.length > 0 && (
                        <optgroup label={getPrivateCustomersLabel(auth.user?.role)}>
                          {privateClients.map((c: any) => (
                            <option key={c._id} value={c._id}>{c.name} {c.email ? `(${c.email})` : ''}</option>
                          ))}
                        </optgroup>
                      )}
                    </select>
                    {loadingClients && <p className="text-xs text-gray-500 mt-2">Cargando clientes...</p>}
                    {!loadingClients && registeredClients.length === 0 && privateClients.length === 0 && (
                      <p className="text-xs text-orange-600 mt-2">
                        No tienes clientes asignados. Contacta con el administrador.
                      </p>
                    )}
                    {selectedClientData && (
                      <div className="mt-3 p-3 bg-green-50 rounded-md border border-green-200">
                        <p className="text-sm text-green-800"><strong>{isDistributor ? 'Destinatario' : 'Cliente'}:</strong> {selectedClientData.name}</p>
                        <p className="text-sm text-green-700">{selectedClientData.email}</p>
                        {selectedClientData.phone && <p className="text-sm text-green-700">Tel: {selectedClientData.phone}</p>}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            <div className="bg-white shadow overflow-hidden sm:rounded-lg p-6 mb-8" data-testid="contact-info-section">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-lg font-medium text-gray-900">
                  <T keyName="checkout.contact_info">Información de contacto</T>
                </h2>
                <div className="text-sm text-gray-500">
                  <T keyName="checkout.client">Cliente</T>: <span className="font-medium text-black">{auth.user?.name}</span>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-4">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                    <T keyName="checkout.first_name">Nombre</T>
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full border ${errors.firstName ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-black focus:border-black sm:text-sm`}
                    data-testid="checkout-first-name-input"
                  />
                  {errors.firstName && <p className="mt-2 text-sm text-red-600" data-testid="checkout-first-name-error"><T keyName={errors.firstName}>{errors.firstName}</T></p>}
                </div>

                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                    <T keyName="checkout.last_name">Apellidos</T>
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full border ${errors.lastName ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-black focus:border-black sm:text-sm`}
                    data-testid="checkout-last-name-input"
                  />
                  {errors.lastName && <p className="mt-2 text-sm text-red-600" data-testid="checkout-last-name-error"><T keyName={errors.lastName}>{errors.lastName}</T></p>}
                </div>

                <div className="sm:col-span-2">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    <T keyName="checkout.email">Email</T>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full border ${errors.email ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-black focus:border-black sm:text-sm`}
                    data-testid="checkout-email-input"
                  />
                  {errors.email && <p className="mt-2 text-sm text-red-600" data-testid="checkout-email-error"><T keyName={errors.email}>{errors.email}</T></p>}
                </div>
              </div>
            </div>

            {isDistributor && (
              <div className="bg-amber-50 shadow overflow-hidden sm:rounded-lg p-6 mb-8 border border-amber-200">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-medium text-amber-950">Envío al cliente final</h2>
                  <label className="flex items-center gap-3 text-sm text-amber-900">
                    <span>Enviar este pedido a un cliente final</span>
                    <input
                      type="checkbox"
                      checked={dropShippingEnabled}
                      onChange={(e) => setDropShippingEnabled(e.target.checked)}
                      className="h-4 w-4"
                    />
                  </label>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                  <div className="rounded-xl bg-white p-4 border border-amber-100">
                    <p className="text-xs uppercase tracking-wide text-gray-500">Facturado a</p>
                    <p className="mt-2 font-medium text-gray-900">{auth.user?.name}</p>
                    <p className="text-sm text-gray-600">{auth.user?.email}</p>
                  </div>
                  <div className="rounded-xl bg-white p-4 border border-amber-100">
                    <p className="text-xs uppercase tracking-wide text-gray-500">Cliente final</p>
                    <p className="mt-2 font-medium text-gray-900">{recipientData?.name || 'Cliente final'}</p>
                    <p className="text-sm text-gray-600">{recipientData?.email || 'Sin email guardado'}</p>
                  </div>
                </div>

                {dropShippingEnabled && (
                  <>
                    <label className="flex items-center gap-3 mb-4 text-sm text-amber-900">
                      <input
                        type="checkbox"
                        checked={notifyRecipient}
                        onChange={(e) => setNotifyRecipient(e.target.checked)}
                        className="h-4 w-4"
                      />
                      <span>
                        Enviar email logístico al cliente final
                        {dropShippingSettings?.recipientEmailsEnabledByDefault && ' (activo por defecto)'}
                      </span>
                    </label>

                    <div className="grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-4">
                      <div>
                        <label htmlFor="recipientName" className="block text-sm font-medium text-gray-700">Nombre destinatario</label>
                        <input
                          id="recipientName"
                          value={recipientData?.name || ''}
                          onChange={(e) => setRecipientData((current: any) => ({ ...(current || {}), name: e.target.value }))}
                          className={`mt-1 block w-full border ${errors.recipientName ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm py-2 px-3`}
                        />
                        {errors.recipientName && <p className="mt-2 text-sm text-red-600">{errors.recipientName}</p>}
                      </div>

                      <div>
                        <label htmlFor="recipientEmail" className="block text-sm font-medium text-gray-700">Email destinatario</label>
                        <input
                          id="recipientEmail"
                          type="email"
                          value={recipientData?.email || ''}
                          onChange={(e) => setRecipientData((current: any) => ({ ...(current || {}), email: e.target.value }))}
                          className={`mt-1 block w-full border ${errors.recipientEmail ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm py-2 px-3`}
                        />
                        {errors.recipientEmail && <p className="mt-2 text-sm text-red-600">{errors.recipientEmail}</p>}
                      </div>

                      <div>
                        <label htmlFor="recipientPhone" className="block text-sm font-medium text-gray-700">Teléfono destinatario</label>
                        <input
                          id="recipientPhone"
                          value={recipientData?.phone || ''}
                          onChange={(e) => setRecipientData((current: any) => ({ ...(current || {}), phone: e.target.value }))}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
                        />
                      </div>

                      <div>
                        <label htmlFor="recipientNotes" className="block text-sm font-medium text-gray-700">Notas para logística</label>
                        <input
                          id="recipientNotes"
                          value={recipientData?.notes || ''}
                          onChange={(e) => setRecipientData((current: any) => ({ ...(current || {}), notes: e.target.value }))}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            <div className="bg-white shadow overflow-hidden sm:rounded-lg p-6 mb-8" data-testid="shipping-address-section">
              <h2 className="text-lg font-medium text-gray-900 mb-6">
                {isDistributor && dropShippingEnabled ? 'Dirección del cliente final' : <T keyName="checkout.shipping_address">Dirección de envío</T>}
              </h2>

              <div className="grid grid-cols-1 gap-y-6 gap-x-4">
                <div className="sm:col-span-2">
                  <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                    <T keyName="checkout.address">Dirección</T>
                  </label>
                  <input
                    type="text"
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full border ${errors.address ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-black focus:border-black sm:text-sm`}
                    data-testid="checkout-address-input"
                  />
                  {errors.address && <p className="mt-2 text-sm text-red-600" data-testid="checkout-address-error"><T keyName={errors.address}>{errors.address}</T></p>}
                </div>

                <div>
                  <label htmlFor="city" className="block text-sm font-medium text-gray-700">
                    <T keyName="checkout.city">Ciudad</T>
                  </label>
                  <input
                    type="text"
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full border ${errors.city ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-black focus:border-black sm:text-sm`}
                    data-testid="checkout-city-input"
                  />
                  {errors.city && <p className="mt-2 text-sm text-red-600" data-testid="checkout-city-error"><T keyName={errors.city}>{errors.city}</T></p>}
                </div>

                <div>
                  <label htmlFor="postalCode" className="block text-sm font-medium text-gray-700">
                    <T keyName="checkout.postal_code">Código postal</T>
                  </label>
                  <input
                    type="text"
                    id="postalCode"
                    name="postalCode"
                    value={formData.postalCode}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full border ${errors.postalCode ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-black focus:border-black sm:text-sm`}
                    data-testid="checkout-postal-code-input"
                  />
                  {errors.postalCode && <p className="mt-2 text-sm text-red-600" data-testid="checkout-postal-code-error"><T keyName={errors.postalCode}>{errors.postalCode}</T></p>}
                </div>

                <div>
                  <label htmlFor="country" className="block text-sm font-medium text-gray-700">
                    <T keyName="checkout.country">País</T>
                  </label>
                  <select
                    id="country"
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-black focus:border-black sm:text-sm"
                    data-testid="checkout-country-select"
                  >
                    <option value="España"><T keyName="checkout.country_spain">España</T></option>
                    <option value="Portugal"><T keyName="checkout.country_portugal">Portugal</T></option>
                    <option value="Francia"><T keyName="checkout.country_france">Francia</T></option>
                    <option value="Alemania"><T keyName="checkout.country_germany">Alemania</T></option>
                    <option value="Italia"><T keyName="checkout.country_italy">Italia</T></option>
                  </select>
                </div>
              </div>
            </div>

            <div className="bg-white shadow overflow-hidden sm:rounded-lg p-6 mb-8">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-medium text-gray-900"><T keyName="checkout.shipping_method">Método de envío</T></h2>
                {isQuoting && (
                  <span className="text-sm text-gray-500"><T keyName="checkout.calculating">Calculando…</T></span>
                )}
              </div>
              <div className="space-y-3">
                <label className="flex items-center justify-between p-3 border rounded-md cursor-pointer">
                  <span className="flex items-center">
                    <input
                      type="radio"
                      name="shippingMethod"
                      value="standard"
                      checked={shippingMethod === 'standard'}
                      onChange={() => setShippingMethod('standard')}
                      className="h-4 w-4 text-black focus:ring-black border-gray-300"
                    />
                    <span className="ml-3">
                      <span className="block text-sm font-medium text-gray-900"><T keyName="checkout.standard_shipping">Estándar</T></span>
                      <span className="block text-sm text-gray-500"><T keyName="checkout.standard_delivery" params={{ days: shippingEtaDays.toString() }}>{`Entrega en aprox. ${shippingEtaDays} días (puede variar según ubicación)`}</T></span>
                    </span>
                  </span>
                  <span className="relative group">
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-200 text-gray-600 text-xs font-bold cursor-help">i</span>
                    <span className="absolute bottom-full right-0 mb-2 w-56 p-2 text-xs text-white bg-gray-800 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                      <T keyName="checkout.shipping_calculated_by_commercial">El dpto. comercial calculará el coste de envío</T>
                    </span>
                  </span>
                </label>
                <label className="flex items-center justify-between p-3 border rounded-md opacity-60 cursor-not-allowed">
                  <span className="flex items-center">
                    <input type="radio" disabled className="h-4 w-4 text-black border-gray-300" />
                    <span className="ml-3">
                      <span className="block text-sm font-medium text-gray-900"><T keyName="checkout.express_soon">Exprés (pronto)</T></span>
                      <span className="block text-sm text-gray-500"><T keyName="checkout.express_not_available">No disponible todavía</T></span>
                    </span>
                  </span>
                  <span className="text-sm font-medium text-gray-400">—</span>
                </label>
              </div>
            </div>

            <div className="bg-white shadow overflow-hidden sm:rounded-lg p-6 mb-8">
              <h2 className="text-lg font-medium text-gray-900 mb-6"><T keyName="checkout.payment_method">Método de pago</T></h2>

              <div className="space-y-4">
                <div className="flex items-center">
                  <input
                    id="bank_transfer"
                    name="paymentMethod"
                    type="radio"
                    value="bank_transfer"
                    checked={formData.paymentMethod === 'bank_transfer'}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-black focus:ring-black border-gray-300"
                  />
                  <label htmlFor="bank_transfer" className="ml-3 block text-sm font-medium text-gray-700">
                    <T keyName="checkout.bank_transfer">Transferencia bancaria</T>
                  </label>
                </div>
              </div>
            </div>

            <div className="bg-white shadow overflow-hidden sm:rounded-lg p-6 mb-8">
              <h2 className="text-lg font-medium text-gray-900 mb-4"><T keyName="checkout.order_notes">Notas del pedido</T></h2>
              <textarea
                value={orderNotes}
                onChange={(e) => setOrderNotes(e.target.value)}
                rows={3}
                maxLength={500}
                className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-black focus:border-black sm:text-sm"
                placeholder="Añade instrucciones especiales, notas de entrega u observaciones sobre tu pedido..."
              />
              <p className="mt-1 text-xs text-gray-400">{orderNotes.length}/500</p>
            </div>

            {errors.form && (
              <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-8">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-700">{errors.form}</p>
                  </div>
                </div>
              </div>
            )}

            <div
              className="bg-amber-50 border-l-4 border-amber-400 p-4 mb-8"
              role="note"
              data-testid="order-modification-notice"
            >
              <p className="text-sm text-amber-900">
                <span className="font-semibold">
                  <T keyName="checkout.order_modification_notice_title">Importante:</T>
                </span>{' '}
                <T keyName="checkout.order_modification_notice">
                  Tu pedido solo puede modificarse durante las primeras 48 horas desde su realización.
                </T>
              </p>
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black mr-3"
                onClick={() => navigate('/cart')}
                data-testid="back-to-cart-button"
              >
                <T keyName="checkout.back_to_cart">Volver al carrito</T>
              </button>
              <button
                type="submit"
                className="bg-black border border-transparent rounded-md shadow-sm py-2 px-4 text-sm font-medium text-white hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black"
                disabled={isSubmitting}
                data-testid="submit-order-button"
              >
                {isSubmitting ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <T keyName="checkout.processing">Procesando...</T>
                  </span>
                ) : <T keyName="checkout.submit_order">Finalizar pedido</T>}
              </button>
            </div>
          </form>
        </div>

        {/* Resumen del pedido */}
        <div className="mt-10 lg:mt-0" data-testid="order-summary">
          <h2 className="text-lg font-medium text-gray-900 mb-6"><T keyName="checkout.order_summary">Resumen del pedido</T></h2>

          <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8">
            <ul className="divide-y divide-gray-200">
              {cart.items.map((item) => (
                <li key={`${item.productId}_${item.variantId || 'default'}`} className="p-4 sm:p-6 flex">
                  <div className="flex-shrink-0 w-20 h-20 bg-gray-200 rounded-md overflow-hidden">
                    <img
                      src={item.imageUrl}
                      alt={item.title}
                      className="w-full h-full object-center object-cover"
                    />
                  </div>
                  <div className="ml-6 flex-1 flex flex-col">
                    <div className="flex justify-between text-base font-medium text-gray-900">
                      <div>
                        <h3>{item.title}</h3>
                        {item.variantTitle && (
                          <p className="text-sm text-gray-600 mt-1">
                            <T keyName="checkout.variant">Variante</T>: {item.variantTitle}
                          </p>
                        )}
                      </div>
                      <div className="ml-4 text-right">
                        {(item as any).priceBeforeDiscount && (item as any).priceBeforeDiscount > item.price ? (
                          <>
                            <p className="text-sm text-gray-400 line-through">€{((item as any).priceBeforeDiscount * item.quantity).toFixed(2)}</p>
                            <p className="font-medium">€{((item.price || 0) * item.quantity).toFixed(2)}</p>
                            {(item as any).discountInfo && (
                              <span className="text-xs font-semibold text-red-600">{(item as any).discountInfo.displayValue}</span>
                            )}
                          </>
                        ) : (
                          <p>€{((item.price || 0) * item.quantity).toFixed(2)}</p>
                        )}
                      </div>
                    </div>
                    <p className="mt-1 text-sm text-gray-500"><T keyName="checkout.quantity">Cantidad</T>: {item.quantity}</p>
                    <p className="mt-1 text-sm text-gray-500">
                      <T keyName="checkout.unit_price">Precio unitario</T>:{' '}
                      {(item as any).priceBeforeDiscount && (item as any).priceBeforeDiscount > item.price ? (
                        <>
                          <span className="line-through text-gray-400 mr-1">€{(item as any).priceBeforeDiscount.toFixed(2)}</span>
                          €{(item.price || 0).toFixed(2)}
                        </>
                      ) : (
                        <>€{(item.price || 0).toFixed(2)}</>
                      )}
                    </p>
                  </div>
                </li>
              ))}
            </ul>

            <div className="border-t border-gray-200 p-6">
              <div className="flex justify-between text-base font-medium text-gray-900 mb-4">
                <p><T keyName="checkout.subtotal">Subtotal</T></p>
                <p data-testid="checkout-subtotal">{cart.subtotal.toFixed(2)}€</p>
              </div>
              {(() => {
                const totalSavings = cart.items.reduce((acc, item) => {
                  const pbd = (item as any).priceBeforeDiscount;
                  if (pbd && pbd > item.price) {
                    return acc + (pbd - item.price) * item.quantity;
                  }
                  return acc;
                }, 0);
                return totalSavings > 0 ? (
                  <div className="flex justify-between text-sm text-green-600 mb-4">
                    <p><T keyName="checkout.discount_applied">Descuento aplicado</T></p>
                    <p data-testid="checkout-savings">-{totalSavings.toFixed(2)}€</p>
                  </div>
                ) : null;
              })()}
              <div className="flex justify-between text-base font-medium text-gray-900 pt-4 border-t border-gray-200">
                <p><T keyName="checkout.total">Total</T></p>
                <p data-testid="checkout-total">{cart.subtotal.toFixed(2)}€</p>
              </div>
              {auth.user?.prontoPagoDiscount != null && auth.user.prontoPagoDiscount > 0 && cart.subtotal > 0 && (
                <div className="flex justify-between mt-3 p-2 bg-amber-50 rounded-md border border-amber-200">
                  <p className="text-sm text-amber-800">
                    <T keyName="checkout.pronto_pago">{`Con pronto pago (${auth.user.prontoPagoDiscount}%)`}</T>
                  </p>
                  <p className="text-sm font-semibold text-amber-800">
                    {(cart.subtotal * (1 - auth.user.prontoPagoDiscount / 100)).toFixed(2)}€
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="bg-gray-50 p-6 sm:rounded-lg">
            <p className="text-sm text-gray-500">
              <T keyName="checkout.terms_prefix">Al completar tu pedido, aceptas nuestros</T>{' '}
              <a href="#" className="text-black font-medium underline">
                <T keyName="checkout.terms_and_conditions">Términos y condiciones</T>
              </a>{' '}
              <T keyName="checkout.and">y</T>{' '}
              <a href="#" className="text-black font-medium underline">
                <T keyName="checkout.privacy_policy">Política de privacidad</T>
              </a>
              .
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage; 
